import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Tabs, TabsList, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import { fmtDate, inr } from "@/lib/utils-format";
import { AlertTriangle, PackageSearch, Boxes, SlidersHorizontal, Save, Store, Trash2 } from "lucide-react";
import { IntakeGrnDialog, turnoverBadge } from "@/components/IntakeGrnDialog";
import { useAuth } from "@/lib/auth";

const HO_ROLES = ["super_admin", "admin", "chief_accountant", "warehouse_manager", "factory_admin"];
const TYPE_LABEL = { retail_showroom: "Retail Showroom", warehouse: "Warehouse", head_office: "Head Office", factory: "Factory", branch: "Retail Branch" };

// Group rows by branch — retail showrooms first (A→Z), then warehouse / factory / others
function groupByBranch(items, branches) {
  const map = new Map();
  for (const r of items) {
    const b = branches.find((x) => x.id === r.branch_id) || {};
    const key = r.branch_id || "unknown";
    if (!map.has(key)) map.set(key, { branch_id: key, name: r.branch_name || b.name || "Unknown branch", code: r.branch_code || b.code, type: r.branch_type || b.type, rows: [] });
    map.get(key).rows.push(r);
  }
  const rank = (t) => (t === "retail_showroom" ? 0 : t === "branch" ? 1 : 2);
  return [...map.values()].sort((a, b) => rank(a.type) - rank(b.type) || (a.name || "").localeCompare(b.name || ""));
}

// Scope selector for HO: all / only retail showrooms / one specific branch
function BranchScopeBar({ branches, scope, setScope, children }) {
  const showrooms = branches.filter((b) => b.type === "retail_showroom");
  const others = branches.filter((b) => b.type !== "retail_showroom");
  return (
    <div className="flex flex-wrap items-center gap-3">
      <select className="h-9 border border-[#EAE5D9] rounded-md px-2 text-sm bg-white" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="stock-intel-scope">
        <option value="showrooms">All retail showrooms</option>
        <option value="all">All branches (incl. warehouse)</option>
        <optgroup label="Retail showrooms">{showrooms.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</optgroup>
        <optgroup label="Warehouse / factory / HO">{others.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</optgroup>
      </select>
      {children}
    </div>
  );
}
const applyScope = (groups, scope) => scope === "all" ? groups : scope === "showrooms" ? groups.filter((g) => g.type === "retail_showroom") : groups.filter((g) => g.branch_id === scope);

function BranchGroup({ g, testid, children }) {
  return (
    <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden" data-testid={`${testid}-group-${g.branch_id}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-[#FDFBF7] border-b border-[#EAE5D9]">
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-[#C05621]" />
          <span className="font-[Outfit] font-semibold">{g.name}</span>
          {g.code && <span className="font-mono text-xs text-[#8B7355]">{g.code}</span>}
          <Badge variant="outline" className="text-[10px]">{TYPE_LABEL[g.type] || g.type || "—"}</Badge>
        </div>
        <Badge variant="outline" className="border-red-300 text-red-700" data-testid={`${testid}-group-count-${g.branch_id}`}>{g.rows.length} item{g.rows.length === 1 ? "" : "s"}</Badge>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const cls = {
    expired: "bg-red-600 text-white border-0",
    critical: "bg-orange-100 text-orange-800 border-orange-200",
    warning: "bg-amber-100 text-amber-800 border-amber-200",
  }[severity] || "bg-[#F7F2E8] text-[#5C4033]";
  return <Badge variant="outline" className={`capitalize ${cls}`}>{severity}</Badge>;
}

function ExpiryTab({ isHO, branches, storeName }) {
  const [days, setDays] = useState(5);
  const [scope, setScope] = useState("showrooms");
  const [data, setData] = useState({ items: [], count: 0 });
  const load = async () => { const { data } = await api.get(`/ho/expiry-alerts?days=${days}`); setData(data); };
  useEffect(() => { load(); }, [days]);
  const groups = applyScope(groupByBranch(data.items, branches), isHO ? scope : "all");
  const shown = groups.reduce((s, g) => s + g.rows.length, 0);
  const table = (rows) => (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left uc-label border-b border-[#EAE5D9]">
          <th className="px-4 py-3">Product</th><th className="px-4 py-3">Batch</th>
          <th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3">Expiry</th><th className="px-4 py-3">Days Left</th><th className="px-4 py-3">Severity</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`expiry-row-${r.branch_id}-${i}`}>
            <td className="px-4 py-3 font-medium">{r.product_name}</td>
            <td className="px-4 py-3 font-mono text-xs">{r.batch_no}</td>
            <td className="px-4 py-3 text-right">{r.quantity}</td>
            <td className="px-4 py-3">{fmtDate(r.expiry_date)}</td>
            <td className="px-4 py-3">{r.days_left}</td>
            <td className="px-4 py-3"><SeverityBadge severity={r.severity} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div className="space-y-4" data-testid="expiry-dashboard-tab">
      <div className="flex flex-wrap items-center gap-3">
        {isHO && <BranchScopeBar branches={branches} scope={scope} setScope={setScope} />}
        <span className="text-sm text-[#8B7355]">Show items expiring within</span>
        <Input type="number" value={days} onChange={(e) => setDays(Number(e.target.value) || 5)} className="w-20 h-8" data-testid="expiry-days-input" />
        <span className="text-sm text-[#8B7355]">days</span>
        <Badge variant="outline" className="ml-auto" data-testid="expiry-count">{shown} batches flagged{isHO ? ` · ${groups.length} store${groups.length === 1 ? "" : "s"}` : storeName ? ` · ${storeName}` : ""}</Badge>
      </div>
      {groups.length === 0 ? (
        <div className="bg-white border border-[#EAE5D9] rounded-lg text-center py-10 text-[#8B7355]" data-testid="expiry-empty">Nothing expiring within {days} days{isHO ? " in the selected stores" : ` at ${storeName || "your store"}`}. All clear.</div>
      ) : isHO ? (
        groups.map((g) => <BranchGroup key={g.branch_id} g={g} testid="expiry">{table(g.rows)}</BranchGroup>)
      ) : (
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">{table(groups[0].rows)}</div>
      )}
    </div>
  );
}

function LowStockTab({ isHO, branches, storeName }) {
  const [scope, setScope] = useState("showrooms");
  const [data, setData] = useState({ items: [], count: 0 });
  useEffect(() => { (async () => { const { data } = await api.get("/ho/low-stock-alerts"); setData(data); })(); }, []);
  const groups = applyScope(groupByBranch(data.items, branches), isHO ? scope : "all");
  const shown = groups.reduce((s, g) => s + g.rows.length, 0);
  const table = (rows) => (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left uc-label border-b border-[#EAE5D9]">
          <th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3 text-right">Current Qty</th><th className="px-4 py-3 text-right">Threshold</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`low-stock-row-${r.branch_id}-${i}`}>
            <td className="px-4 py-3 font-medium">{r.product_name}</td>
            <td className="px-4 py-3 text-xs">{r.sku}</td>
            <td className="px-4 py-3 text-right text-red-600 font-semibold">{r.quantity} {r.unit}</td>
            <td className="px-4 py-3 text-right">{r.low_threshold}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div className="space-y-4" data-testid="low-stock-tab">
      <div className="flex flex-wrap items-center gap-3">
        {isHO && <BranchScopeBar branches={branches} scope={scope} setScope={setScope} />}
        <Badge variant="outline" className="ml-auto border-red-300 text-red-700" data-testid="low-stock-count">
          {shown} low-stock alert{shown === 1 ? "" : "s"}{isHO ? ` across ${groups.length} store${groups.length === 1 ? "" : "s"}` : storeName ? ` · ${storeName}` : ""}
        </Badge>
      </div>
      {groups.length === 0 ? (
        <div className="bg-white border border-[#EAE5D9] rounded-lg text-center py-10 text-[#8B7355]" data-testid="low-stock-empty">No low-stock alerts right now{isHO ? " in the selected stores" : ` at ${storeName || "your store"}`}.</div>
      ) : isHO ? (
        groups.map((g) => <BranchGroup key={g.branch_id} g={g} testid="low-stock">{table(g.rows)}</BranchGroup>)
      ) : (
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">{table(groups[0].rows)}</div>
      )}
    </div>
  );
}

function SkuReportTab({ isHO, branches, storeName }) {
  const [scope, setScope] = useState("showrooms");
  const [data, setData] = useState({ items: [], count: 0 });
  useEffect(() => { (async () => { const { data } = await api.get("/ho/sku-report"); setData(data); })(); }, []);
  const groups = applyScope(groupByBranch(data.items, branches), isHO ? scope : "all");
  const shown = groups.reduce((s, g) => s + g.rows.length, 0);
  const table = (rows) => (
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-white">
        <tr className="text-left uc-label border-b border-[#EAE5D9]">
          <th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3">Batch</th><th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3">Expiry</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`sku-report-row-${r.branch_id}-${i}`}>
            <td className="px-4 py-3 font-medium">{r.product_name}</td>
            <td className="px-4 py-3 text-xs">{r.sku}</td>
            <td className="px-4 py-3 font-mono text-xs">{r.batch_no || (r.tracked ? "—" : "not batch-tracked")}</td>
            <td className="px-4 py-3 text-right">{r.quantity}</td>
            <td className="px-4 py-3">{r.expiry_date ? fmtDate(r.expiry_date) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div className="space-y-4" data-testid="sku-report-tab">
      <div className="flex flex-wrap items-center gap-3">
        {isHO && <BranchScopeBar branches={branches} scope={scope} setScope={setScope} />}
        <Badge variant="outline" className="ml-auto" data-testid="sku-report-count">{shown} rows{isHO ? ` · ${groups.length} store${groups.length === 1 ? "" : "s"}` : storeName ? ` · ${storeName}` : ""}</Badge>
      </div>
      {groups.length === 0 ? (
        <div className="bg-white border border-[#EAE5D9] rounded-lg text-center py-10 text-[#8B7355]" data-testid="sku-report-empty">No stock rows{isHO ? " in the selected stores" : ` at ${storeName || "your store"}`}.</div>
      ) : isHO ? (
        groups.map((g) => <BranchGroup key={g.branch_id} g={g} testid="sku-report"><div className="max-h-[480px] overflow-y-auto">{table(g.rows)}</div></BranchGroup>)
      ) : (
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto max-h-[600px] overflow-y-auto">{table(groups[0].rows)}</div>
      )}
    </div>
  );
}

const ymd = (d) => d.toISOString().slice(0, 10);

function DisposalReportTab({ isHO, branches }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [scope, setScope] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { date_from: from, date_to: to };
    if (scope) params.branch_id = scope;
    api.get("/disposals/report", { params }).then((r) => setData(r.data)).catch((e) => { toast.error(e?.response?.data?.detail || "Failed to load disposal report"); setData({ rows: [], by_branch: [], totals: {} }); });
  }, [from, to, scope]);
  return (
    <div className="space-y-4" data-testid="disposal-report">
      <div className="flex flex-wrap items-end gap-3">
        <div><div className="uc-label mb-1">From</div><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="disposal-report-from" /></div>
        <div><div className="uc-label mb-1">To</div><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="disposal-report-to" /></div>
        {isHO && (
          <div><div className="uc-label mb-1">Location</div>
            <select className="h-9 border border-[#EAE5D9] rounded-md px-2 text-sm bg-white" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="disposal-report-scope">
              <option value="">All locations</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
            </select></div>
        )}
        {data && (
          <div className="ml-auto flex gap-2" data-testid="disposal-report-totals">
            <Badge variant="outline">{data.totals.count || 0} disposal{data.totals.count === 1 ? "" : "s"}</Badge>
            <Badge variant="outline">Qty {data.totals.quantity || 0}</Badge>
            <Badge className="bg-red-600 text-white">Value {inr(data.totals.value || 0)}</Badge>
          </div>
        )}
      </div>
      {data?.by_branch?.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {data.by_branch.map((g) => <div key={g.branch_id} className="bg-white border border-[#EAE5D9] rounded px-3 py-1.5 text-xs" data-testid={`disposal-report-branch-${g.branch_id}`}><b>{g.branch_name}</b> · {g.count} · qty {g.quantity} · {inr(g.value)}</div>)}
        </div>
      )}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="disposal-report-table">
          <thead><tr className="text-left uc-label border-b border-[#EAE5D9]"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Batch</th><th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3 text-right">Rate</th><th className="px-4 py-3 text-right">Value</th><th className="px-4 py-3">Reason / Approved by</th></tr></thead>
          <tbody>
            {!data && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#8B7355]">Loading…</td></tr>}
            {data?.rows?.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#8B7355]" data-testid="disposal-report-empty">No approved disposals in this period.</td></tr>}
            {data?.rows?.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9] align-top" data-testid={`disposal-report-row-${r.id}`}>
                <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.decided_at)}</td>
                <td className="px-4 py-2">{r.branch_name}</td>
                <td className="px-4 py-2"><div className="font-medium">{r.product_name}</div><div className="text-xs text-[#8B7355]">{r.sku}</div></td>
                <td className="px-4 py-2 font-mono text-xs">{r.batch_no}<div className="text-[#8B7355]">exp {fmtDate(r.expiry_date)}</div></td>
                <td className="px-4 py-2 text-right">{r.quantity} {r.unit}</td>
                <td className="px-4 py-2 text-right">{inr(r.rate)}</td>
                <td className="px-4 py-2 text-right font-semibold text-red-700">{inr(r.value)}</td>
                <td className="px-4 py-2 text-xs max-w-[240px]">{r.reason}<div className="text-[#8B7355] mt-0.5">{r.requested_by_name} → {r.decided_by_name} · {r.number}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ShowroomIntakeTab() {
  const [range, setRange] = useState("month");
  const [from, setFrom] = useState(ymd(new Date(Date.now() - 6 * 864e5)));
  const [to, setTo] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const load = async () => {
    const params = range === "custom" ? { range, date_from: from, date_to: to } : { range };
    try { const { data } = await api.get("/ho/showroom-intake", { params }); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load intake report"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, from, to]);
  const rows = data?.rows || [];
  const t = data?.totals;
  return (
    <div className="space-y-4" data-testid="showroom-intake-tab">
      <div className="flex flex-wrap items-center gap-2">
        {[["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]].map(([v, l]) => (
          <Button key={v} size="sm" variant={range === v ? "default" : "outline"} onClick={() => setRange(v)}
            className={range === v ? "bg-[#C05621] hover:bg-[#9C4221] text-white" : ""} data-testid={`intake-range-${v}`}>{l}</Button>
        ))}
        {range === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 bg-white" data-testid="intake-date-from" />
            <span className="text-[#8B7355]">→</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 bg-white" data-testid="intake-date-to" />
          </div>
        )}
        {data && <span className="text-xs text-[#8B7355] ml-auto" data-testid="intake-period-label">{fmtDate(data.date_from)} → {fmtDate(data.date_to)} · value at HO Transfer Rate · low turnover &lt; {Math.round((data.low_turnover_threshold || 0.5) * 100)}%</span>}
      </div>
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="intake-totals">
          {[["GRNs received", t.grn_count], ["Units received", t.qty_received], ["Intake value", inr(t.value_received)], ["Sales value", inr(t.sales_value)]].map(([l, v]) => (
            <div key={l} className="bg-white border border-[#EAE5D9] rounded-lg p-3"><div className="uc-label">{l}</div><div className="font-[Outfit] text-xl font-semibold">{v}</div></div>
          ))}
        </div>
      )}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left uc-label border-b border-[#EAE5D9]">
            <th className="px-4 py-3">Showroom</th><th className="px-4 py-3 text-right">GRNs</th><th className="px-4 py-3 text-right">Units received</th><th className="px-4 py-3 text-right">Intake value</th>
            <th className="px-4 py-3 text-right">Units sold</th><th className="px-4 py-3 text-right">Sales value</th><th className="px-4 py-3 text-right">Net (intake − sales)</th><th className="px-4 py-3">Sales-to-intake</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => { const b = turnoverBadge(r); return (
              <tr key={r.branch_id} className={`border-b border-[#EAE5D9] last:border-0 ${r.turnover === "low" ? "bg-red-50/40" : ""}`} data-testid={`intake-row-${r.branch_id}`}>
                <td className="px-4 py-3 font-medium">{r.branch_name} <span className="text-[10px] font-mono text-[#8B7355]">{r.branch_code}</span></td>
                <td className="px-4 py-3 text-right">{r.grn_count}</td>
                <td className="px-4 py-3 text-right">{r.qty_received}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setDrill(r)} className="font-semibold underline decoration-dotted underline-offset-4 hover:text-[#C05621]" data-testid={`intake-value-${r.branch_id}`} title="View GRNs">{inr(r.value_received)}</button>
                </td>
                <td className="px-4 py-3 text-right">{r.qty_sold}</td>
                <td className="px-4 py-3 text-right text-emerald-700 font-medium">{inr(r.sales_value)}</td>
                <td className={`px-4 py-3 text-right font-medium ${r.net_value > 0 ? "text-amber-700" : "text-emerald-700"}`}>{r.net_qty} u · {inr(r.net_value)}</td>
                <td className="px-4 py-3"><Badge variant="outline" className={b.cls} data-testid={`intake-turnover-${r.branch_id}`}>{b.label}</Badge></td>
              </tr>
            ); })}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8B7355]">No showrooms found.</td></tr>}
          </tbody>
        </table>
      </div>
      <IntakeGrnDialog branch={drill} dateFrom={data?.date_from?.slice(0, 10)} dateTo={data?.date_to?.slice(0, 10)} onClose={() => setDrill(null)} />
    </div>
  );
}

function ReorderPointsTab() {
  const [data, setData] = useState({ showrooms: [], products: [] });
  const [search, setSearch] = useState("");
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await api.get(`/ho/reorder-points${search ? `?search=${encodeURIComponent(search)}` : ""}`);
    setData(data); setDirty({});
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);
  const key = (pid, bid) => `${pid}|${bid}`;
  const valueOf = (p, bid) => dirty[key(p.id, bid)] ?? (p.points?.[bid]?.low_threshold ?? "");
  const setCell = (pid, bid, v) => setDirty((d) => ({ ...d, [key(pid, bid)]: v }));
  const applyAll = (p, v) => {
    if (v === "" || v === null || isNaN(Number(v))) return toast.error("Enter a reorder value first");
    setDirty((d) => { const n = { ...d }; data.showrooms.forEach((s) => { n[key(p.id, s.id)] = v; }); return n; });
  };
  const dirtyCount = Object.keys(dirty).length;
  const save = async () => {
    const items = Object.entries(dirty).map(([k, v]) => { const [product_id, branch_id] = k.split("|"); return { product_id, branch_id, low_threshold: Number(v) || 0 }; });
    if (items.some((i) => i.low_threshold < 0)) return toast.error("Reorder point cannot be negative");
    setSaving(true);
    try { await api.put("/ho/reorder-points", { items }); toast.success(`Saved ${items.length} reorder point(s)`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="reorder-points-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product / SKU…" className="max-w-xs bg-white" data-testid="reorder-points-search" />
        <div className="text-xs text-[#8B7355]">Blank / 0 = fall back to the product's global "Low-stock at" value.</div>
        <div className="ml-auto flex items-center gap-2">
          {dirtyCount > 0 && <Badge variant="outline" className="border-amber-300 text-amber-700" data-testid="reorder-points-dirty-count">{dirtyCount} unsaved</Badge>}
          <Button onClick={save} disabled={!dirtyCount || saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="reorder-points-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto max-h-[640px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3 min-w-[220px]">Product</th>
              <th className="px-4 py-3 text-right">Global</th>
              {data.showrooms.map((s) => <th key={s.id} className="px-3 py-3 text-right min-w-[120px]" title={s.name}>{s.code || s.name}</th>)}
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.products.map((p) => (
              <tr key={p.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`reorder-row-${p.id}`}>
                <td className="px-4 py-2"><div className="font-medium">{p.name}</div><div className="text-[10px] font-mono text-[#8B7355]">{p.sku}</div></td>
                <td className="px-4 py-2 text-right text-[#8B7355]">{p.global_threshold}</td>
                {data.showrooms.map((s) => {
                  const cell = p.points?.[s.id];
                  const isDirty = dirty[key(p.id, s.id)] !== undefined;
                  return (
                    <td key={s.id} className="px-2 py-1.5">
                      <input type="number" min="0" value={valueOf(p, s.id)} onChange={(e) => setCell(p.id, s.id, e.target.value)}
                        placeholder={String(p.global_threshold)}
                        className={`w-24 text-right border rounded px-2 py-1 text-sm ${isDirty ? "border-amber-400 bg-amber-50" : "border-[#EAE5D9]"}`}
                        data-testid={`reorder-cell-${p.id}-${s.id}`} />
                      <div className="text-[10px] text-right text-[#8B7355]">qty {cell ? cell.quantity : "—"}</div>
                    </td>
                  );
                })}
                <td className="px-2 py-1.5">
                  <ApplyAll onApply={(v) => applyAll(p, v)} pid={p.id} />
                </td>
              </tr>
            ))}
            {data.products.length === 0 && <tr><td colSpan={3 + data.showrooms.length} className="text-center py-10 text-[#8B7355]">No products found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApplyAll({ onApply, pid }) {
  const [v, setV] = useState("");
  return (
    <div className="flex items-center gap-1">
      <input type="number" min="0" value={v} onChange={(e) => setV(e.target.value)} placeholder="all" className="w-16 text-right border border-[#EAE5D9] rounded px-2 py-1 text-xs" data-testid={`reorder-apply-all-input-${pid}`} />
      <button onClick={() => onApply(v)} className="text-xs text-[#C05621] hover:underline whitespace-nowrap" data-testid={`reorder-apply-all-btn-${pid}`}>Apply to all</button>
    </div>
  );
}

export default function HOReportsPage() {
  const [tab, setTab] = useState("expiry");
  const { user } = useAuth();
  const isHO = HO_ROLES.includes(user?.role_id || user?.role);
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, []);
  const store = !isHO ? branches.find((b) => b.id === user?.branch_id) : null;
  const scoped = { isHO, branches, storeName: store?.name };
  return (
    <div className="space-y-6" data-testid="ho-reports-page">
      <div>
        <div className="uc-label">{isHO ? "Head Office" : store?.name || "Your Store"}</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Stock Intelligence</h1>
        <p className="text-sm text-[#8B7355] mt-1">
          {isHO ? "Expiry alerts, low-stock and store-wise SKU visibility — grouped by retail showroom." : `Expiry alerts, low-stock and stock on hand for ${store?.name || "your store"} only.`}
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent flex-wrap h-auto p-0 gap-1">
          <ColorTab value="expiry" tone="orange" icon={AlertTriangle} testid="ho-reports-tab-expiry">Expiry Dashboard</ColorTab>
          <ColorTab value="low-stock" tone="red" icon={Boxes} testid="ho-reports-tab-low-stock">Low Stock Alerts</ColorTab>
          <ColorTab value="sku" tone="blue" icon={PackageSearch} testid="ho-reports-tab-sku">Store-wise SKU Report</ColorTab>
          {isHO && <ColorTab value="reorder" tone="green" icon={SlidersHorizontal} testid="ho-reports-tab-reorder">Reorder Points</ColorTab>}
          {isHO && <ColorTab value="intake" tone="amber" icon={Store} testid="ho-reports-tab-intake">Showroom Intake</ColorTab>}
          <ColorTab value="disposals" tone="slate" icon={Trash2} testid="ho-reports-tab-disposals">Disposal Report</ColorTab>
        </TabsList>
        <TabsContent value="expiry" className="pt-4"><ExpiryTab {...scoped} /></TabsContent>
        <TabsContent value="low-stock" className="pt-4"><LowStockTab {...scoped} /></TabsContent>
        <TabsContent value="sku" className="pt-4"><SkuReportTab {...scoped} /></TabsContent>
        {isHO && <TabsContent value="reorder" className="pt-4"><ReorderPointsTab /></TabsContent>}
        {isHO && <TabsContent value="intake" className="pt-4"><ShowroomIntakeTab /></TabsContent>}
        <TabsContent value="disposals" className="pt-4"><DisposalReportTab isHO={isHO} branches={branches} /></TabsContent>
      </Tabs>
    </div>
  );
}
